import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Star, Loader2, PartyPopper } from "lucide-react";
import Navbar from "../components/marketing/Navbar";
import Footer from "../components/marketing/Footer";
import { api, apiErrorMessage } from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

const PLANS = [
  {
    name: "Starter",
    tier: "starter",
    monthly: 0, yearly: 0,
    tagline: "For a single consultant, tracking time on up to two projects.",
    features: ["1 seat", "2 projects", "Unlimited invoices", "Time tracking + reports", "Xero draft push"],
    cta: "Get started free",
    lookup_monthly: null, lookup_yearly: null,
  },
  {
    name: "Team",
    tier: "team",
    monthly: 12, yearly: 9.60,
    tagline: "For consulting teams that plan, manage and monitor multiple projects.",
    features: ["Unlimited seats", "Unlimited projects", "Weekly resource planning", "Plan vs actual", "Xero + invoicing", "Rate cards & tiers"],
    cta: "Start 14-day trial",
    featured: true,
    lookup_monthly: "team_monthly", lookup_yearly: "team_yearly",
  },
  {
    name: "Business",
    tier: "business",
    monthly: 24, yearly: 19.20,
    tagline: "For growing consultancies needing deeper controls and priority support.",
    features: ["Everything in Team", "Approval workflows", "Custom rate tiers", "Priority support", "Advanced reports"],
    cta: "Start 14-day trial",
    lookup_monthly: "business_monthly", lookup_yearly: "business_yearly",
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const [loading, setLoading] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const isWelcome = params.get("welcome") === "1";

  const startCheckout = async (plan) => {
    if (!plan.lookup_monthly) {
      // Starter — free tier. Signed-in users already have it, signed-out users go to signup.
      nav(user ? "/app" : "/signup");
      return;
    }
    if (!user) {
      toast({ title: "Sign in first", description: "Create your workspace, then start the 14-day trial." });
      nav(`/signup?next=/pricing&plan=${plan.tier}&annual=${annual ? 1 : 0}`);
      return;
    }
    setLoading(plan.tier);
    try {
      const key = annual ? plan.lookup_yearly : plan.lookup_monthly;
      const { checkout_url } = await api.billingCheckout(key, 1);
      window.location.href = checkout_url;
    } catch (e) {
      toast({ title: "Checkout failed", description: apiErrorMessage(e) });
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      {isWelcome && (
        <div className="bg-coral text-white px-4 py-3" data-testid="welcome-banner">
          <div className="max-w-7xl mx-auto flex items-center gap-3 text-sm">
            <PartyPopper size={16} />
            <div>
              <b>Welcome to Full Bench, {user?.name?.split(" ")[0] || "there"}.</b>{" "}
              Start your 14-day free trial on Team or Business (card required, no charge until day 15),
              or keep the free Starter plan.
            </div>
            <button
              onClick={() => nav("/app")}
              data-testid="skip-to-app"
              className="ml-auto text-xs bg-white/15 hover:bg-white/25 rounded-md px-3 py-1.5 font-medium whitespace-nowrap"
            >
              Continue on Starter (free) →
            </button>
          </div>
        </div>
      )}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-16 pb-6 text-center">
        <div className="text-sm text-coral font-medium">Pricing</div>
        <h1 className="font-serif-h text-5xl md:text-6xl mt-3">Simple pricing that scales with your team.</h1>
        <p className="mt-4 text-[#3a4b45] max-w-2xl mx-auto">Free for a single person. Pay per seat when you're ready to grow. Cancel anytime.</p>

        <div className="mt-8 inline-flex items-center gap-1 rounded-full bg-white border border-cream p-1" data-testid="pricing-toggle">
          <button
            onClick={() => setAnnual(false)}
            data-testid="pricing-monthly"
            className={`px-4 py-1.5 rounded-full text-sm font-medium ${!annual ? "bg-forest text-white" : "text-forest/70"}`}
          >Monthly</button>
          <button
            onClick={() => setAnnual(true)}
            data-testid="pricing-annual"
            className={`px-4 py-1.5 rounded-full text-sm font-medium ${annual ? "bg-forest text-white" : "text-forest/70"}`}
          >
            Annual <span className="text-coral text-xs">save 20%</span>
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20 grid md:grid-cols-3 gap-6">
        {PLANS.map((p) => {
          const price = annual ? p.yearly : p.monthly;
          return (
            <div
              key={p.name}
              className={`bg-white rounded-2xl border p-8 card-shadow relative ${
                p.featured ? "border-coral ring-2 ring-coral/30" : "border-cream"
              }`}
              data-testid={`plan-card-${p.tier}`}
            >
              {p.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-coral text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                  <Star size={12} /> Most popular
                </div>
              )}
              <div className="text-sm text-[#6a736f]">{p.name}</div>
              <div className="mt-2">
                <span className="font-serif-h text-5xl">${price}</span>
                {price > 0 && (
                  <span className="text-sm text-[#6a736f] ml-1">
                    per seat / {annual ? "month, billed annually" : "month"}
                  </span>
                )}
              </div>
              <p className="mt-3 text-sm text-[#3a4b45]">{p.tagline}</p>
              <button
                onClick={() => startCheckout(p)}
                disabled={loading === p.tier}
                data-testid={`plan-cta-${p.tier}`}
                className={`mt-6 w-full text-center px-4 py-2.5 rounded-md text-sm font-medium flex items-center justify-center gap-2 ${
                  p.featured ? "btn-coral" : "btn-outline-forest"
                } disabled:opacity-60`}
              >
                {loading === p.tier && <Loader2 size={14} className="animate-spin" />}
                {p.cta}
              </button>
              <ul className="mt-6 space-y-2 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={14} className="mt-1 text-coral flex-shrink-0" /> <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>

      <div className="text-center text-xs text-forest/60 pb-12">
        14-day trial on Team &amp; Business. Card required, no charge until day 15.
        Cancel or downgrade anytime from Settings → Billing.
      </div>

      <Footer />
    </div>
  );
}
